/**
 * Self-Cert Individual XML Generator
 * Updated implementation using user-provided XML template with form field mappings
 */

import { SelfCertIndividualService } from "../../../services/SelfCertIndividualService";
import { SelfCertIndividualFormModel } from "../../../Models/SelfCertIndividualModels";
import { getPDFBase64 } from "./generateW8BENXML";
import { getFullStateName } from "../../../utils/formUtils";

export async function generateSelfCertIndividualXML(data: any): Promise<string> {
  // Check if form data needs mapping
  let formModel: SelfCertIndividualFormModel;
  let isMapDone = data?.isMapDone || false;

  if (!isMapDone) {
    formModel = SelfCertIndividualService.fromApiResponse(data);
  } else {
    formModel = new SelfCertIndividualFormModel(data);
  }

  // Helper function to get field values
  const getValue = (fieldName: string): string => {
    const value = formModel[fieldName] || "";
    return value;
  };

  // Helper function to convert yes/no values to true/false
  const convertYesNoToBoolean = (fieldName: string): string => {
    const value = formModel[fieldName];
    if (
      value === true ||
      value === "yes" ||
      value === "Yes" ||
      value === "selected" ||
      value === "true"
    ) {
      return "true";
    }
    if (
      value === false ||
      value === "no" ||
      value === "No" ||
      value === "" ||
      value === null ||
      value === undefined
    ) {
      return "false";
    }
    return "false"; // Default to false for any other value
  };

  // Generate original file XML if viewPdf is provided
  const generateOriginalFileXml = async () => {
    const viewPdfUrl = getValue("viewPdf");
    if (viewPdfUrl) {
      try {
        const originalFileContent = await getPDFBase64(viewPdfUrl, formModel["fileImportId"]);
        const fileName = viewPdfUrl.split("/").pop() || "original_document.pdf";

        return `
        <OriginalFile>
          <Name>${fileName}</Name>
          <Data>${originalFileContent}</Data>
        </OriginalFile>`;
      } catch (error) {
        console.error("Error converting original PDF to base64:", error);
        return "<OriginalFile></OriginalFile>";
      }
    }
    return "<OriginalFile></OriginalFile>";
  };

  const originalFileXml = await generateOriginalFileXml();

  // Generate XML using the user-provided template with proper form field mappings
  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<Deloitte_US_Individual_2024>
    <BusinessUnit></BusinessUnit>
    <UniqueIdentifier>${getValue("UniqueIdentifier")}</UniqueIdentifier>
    <CommonInfo>
        <Individual>
            <TitleName>${getValue("SC-Individual-Title")}</TitleName>
            <FirstName>${getValue("SC-Individual-First_name")}</FirstName>
            <LastName>${getValue("SC-Individual-Last_name")}</LastName>
            <MiddleName>${getValue("SC-Individual-Middle_name")}</MiddleName>
            <CountryOfBirth>${getValue("SC-Individual-Country_of_birth")}</CountryOfBirth>
            <CountryOfBirthOther>${getValue("SC-Individual-Country_of_birth_other")}</CountryOfBirthOther>
            <DateOfBirth>${getValue("SC-Individual-Date_of_birth")}</DateOfBirth>
            <CityTownOfBirth>${getValue("SC-Individual-City_of_birth")}</CityTownOfBirth>
        </Individual>
        <PermanentAddress>
            <Line1>${getValue("SC-Individual-Current_residence_address_street")}</Line1>
            <Line2>${getValue("SC-Individual-Current_residence_address_line2")}</Line2>
            <City_Town>${getValue("SC-Individual-Current_residence_address_city")}</City_Town>
            <State_Province>${getFullStateName(getValue("SC-Individual-Current_residence_address_state"))}</State_Province>
            <Country>${getValue("SC-Individual-Current_residence_address_country")}</Country>
            <CountryOther>${getValue("SC-Individual-Current_residence_address_country_other")}</CountryOther>
            <Zip_PostalCode>${getValue("SC-Individual-Current_residence_address_postal")}</Zip_PostalCode>
            <IsCare_of_address>${convertYesNoToBoolean("SC-Individual-Current_residence_care_of")}</IsCare_of_address>
            <IsP_O_Box>${convertYesNoToBoolean("SC-Individual-Current_residence_po_box")}</IsP_O_Box>
            <IsRuralRoute>${convertYesNoToBoolean("SC-Individual-Current_residence_rural_route")}</IsRuralRoute>
        </PermanentAddress>
        <MailingAddress>
            <Line1>${getValue("SC-Individual-Mailing_address_street")}</Line1>
            <Line2>${getValue("SC-Individual-Mailing_address_line2")}</Line2>
            <City_Town>${getValue("SC-Individual-Mailing_address_city")}</City_Town>
            <State_Province>${getFullStateName(getValue("SC-Individual-Mailing_address_state"))}</State_Province>
            <Country>${getValue("SC-Individual-Mailing_address_country")}</Country>
            <CountryOther>${getValue("SC-Individual-Mailing_address_country_other")}</CountryOther>
            <Zip_PostalCode>${getValue("SC-Individual-Mailing_address_postal")}</Zip_PostalCode>
            <IsCare_of_address>${convertYesNoToBoolean("SC-Individual-Mailing_care_of")}</IsCare_of_address>
            <IsP_O_Box>${convertYesNoToBoolean("SC-Individual-Mailing_po_box")}</IsP_O_Box>
            <IsRuralRoute>${convertYesNoToBoolean("SC-Individual-Mailing_rural_route")}</IsRuralRoute>
        </MailingAddress>
    </CommonInfo>
    <USCitizen>${convertYesNoToBoolean("SC-Individual-US_citizen")}</USCitizen>
    <TaxResidencyCountry>${getValue("SC-Individual-Tax_residency_country")}</TaxResidencyCountry>
    <TaxResidencyTINType>${getValue("SC-Individual-Tax_reference_number_type")}</TaxResidencyTINType>
    <TaxResidencyTIN>${getValue("SC-Individual-Tax_id")}</TaxResidencyTIN>
    <cbTaxResidencyYes>${convertYesNoToBoolean("SC-Individual-Sole_residency_yes")}</cbTaxResidencyYes>
    <cbTaxResidencyNo>${convertYesNoToBoolean("SC-Individual-Sole_residency_no")}</cbTaxResidencyNo>
    <cbTaxResidencyTINNotAvailable>${convertYesNoToBoolean("SC-Individual-Tin_or_functional_equivalent")}</cbTaxResidencyTINNotAvailable>
    <TaxResidencyTINNotAvailableReason>${getValue("SC-Individual-Reason_for_non_tin")}</TaxResidencyTINNotAvailableReason>
    <taxCountry1>${getValue("sc_cp Jurisdiction 2")}</taxCountry1>
     <taxTIN1>${getValue("sc_cp Tax Reference Number Type 2")}</taxTIN1>
    <taxCountry1IDType>${getValue("sc_cp Tax Identification Number 2")}</taxCountry1IDType>
     <taxCountry1ID>${getValue("sc_cp Reason for non-availability of TIN 2")}</taxCountry1ID>
    <taxCountry2>${getValue("sc_cp Jurisdiction 3")}</taxCountry2>
     <taxTIN2>${getValue("sc_cp Tax Reference Number Type 3")}</taxTIN2>
    <taxCountry2IDType>${getValue("sc_cp Tax Identification Number 3")}</taxCountry2IDType>
    <taxCountry2ID>${getValue("sc_cp Reason for non-availability of TIN 3")}</taxCountry2ID>
    <taxCountry3>${getValue("sc_cp Jurisdiction 4")}</taxCountry3>
    <taxTIN3>${getValue("sc_cp Tax Reference Number Type 4")}</taxTIN3>
    <taxCountry3IDType>${getValue("sc_cp Tax Identification Number 4")}</taxCountry3IDType>
     <taxCountry3ID>${getValue("sc_cp Reason for non-availability of TIN 4")}</taxCountry3ID>
    
    <AccountID1>${getValue("SC-Individual-Account_ID_1")}</AccountID1>
    <AccountID2>${getValue("SC-Individual-Account_ID_2")}</AccountID2>
    <AccountID3>${getValue("SC-Individual-Account_ID_3")}</AccountID3>
    <Intro>${getValue("Form_name")}</Intro>
    <EntityCertification>${convertYesNoToBoolean("sc_cp Certify Account Holder")}</EntityCertification>
    <NotResidentForTax>${convertYesNoToBoolean("SC-Individual-Not_resident_for_tax")}</NotResidentForTax>
    <USTIN>${getValue("SC-Individual-US_TIN")}</USTIN>
    <TinUnavailableReason>${getValue("sc_cp Reason for unable to procure TIN")}</TinUnavailableReason>
    <SignInfo>
        <IsSigned>${convertYesNoToBoolean("SC-Individual-isSigned")}</IsSigned>
        <SignatureUnreadable>${convertYesNoToBoolean("SC-Individual-Signature_unreadable")}</SignatureUnreadable>
        <By>${getValue("SC-Individual-Print_name")}</By>
        <Capacity>${getValue("SC-Individual-Capacity")}</Capacity>
        <SignatureUnreadable>${convertYesNoToBoolean("Signature_unreadable")}</SignatureUnreadable>
        <CapacityOther>${getValue("SC-Individual-Capacity_other")}</CapacityOther>
        <Date>${getValue("SC-Individual-Date")}</Date>
        <DateOnForm>${getValue("SC-Individual-Date")}</DateOnForm>
    </SignInfo>
    ${originalFileXml}
    <Form_OCR_Signature>${getValue("SC-Individual-Signature")}</Form_OCR_Signature>
</Deloitte_US_Individual_2024>`;

  console.log("🎯 generateSelfCertIndividualXML final XML:", xml);
  return xml;
}
